import React, { useState, useEffect, useCallback } from 'react';
import { 
  Save, Download, Upload, History, Plus, Trash2, GripVertical,
  ChevronDown, ChevronUp, Edit2, Check, X, AlertCircle,
  Calculator, Package, Settings, RefreshCw, Copy, Search,
  Smartphone, Battery, Monitor, Wifi, Camera, Volume2, 
  Wrench, PlugZap, Code, Box
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Category icons map
const CATEGORY_ICONS = {
  'device_status': Smartphone,
  'display': Monitor,
  'battery': Battery,
  'network': Wifi,
  'camera': Camera,
  'audio_sensors': Volume2,
  'physical': Wrench,
  'ports': PlugZap,
  'software': Code,
  'packaging': Box
};

// Severity colors
const SEVERITY_COLORS = {
  'Good': 'bg-green-100 text-green-700 border-green-200',
  'Low': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  'Medium': 'bg-orange-100 text-orange-700 border-orange-200',
  'High': 'bg-red-100 text-red-700 border-red-200',
  'Salvage': 'bg-gray-800 text-white border-gray-800'
};

// Grade colors
const GRADE_COLORS = {
  'Z1': 'bg-green-500',
  'Z2': 'bg-green-400',
  'Z3': 'bg-blue-500',
  'Z4': 'bg-blue-400',
  'Z5': 'bg-yellow-500',
  'Z6': 'bg-orange-500',
  'Z7': 'bg-red-500'
};

// ==================== Inline Edit Component ====================
const InlineEdit = ({ value, onSave, type = 'text', suffix = '' }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(type === 'number' ? parseFloat(editValue) : editValue);
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-20 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          autoFocus
          step={type === 'number' ? '0.01' : undefined}
        />
        <button onClick={handleSave} className="p-1 text-green-600 hover:bg-green-50 rounded">
          <Check className="w-4 h-4" />
        </button>
        <button onClick={() => setEditing(false)} className="p-1 text-red-600 hover:bg-red-50 rounded">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="px-2 py-1 hover:bg-gray-100 rounded cursor-pointer group flex items-center gap-1"
    >
      <span>{value}{suffix}</span>
      <Edit2 className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100" />
    </button>
  );
};

// ==================== Option Edit Modal ====================
const OptionEditModal = ({ option, onClose, onSave, gradeHierarchy }) => {
  const [formData, setFormData] = useState({
    text: option.text || '',
    part: option.part || '',
    severity: option.severity || 'Good',
    weight: option.weight || 0,
    multiplier: option.multiplier || 0,
    grades: option.grades || gradeHierarchy,
    topup: option.topup || null,
    fixed_price: option.fixed_price || null
  });

  const handleGradeToggle = (grade) => {
    setFormData(prev => ({
      ...prev,
      grades: prev.grades.includes(grade)
        ? prev.grades.filter(g => g !== grade)
        : [...prev.grades, grade].sort((a, b) => gradeHierarchy.indexOf(a) - gradeHierarchy.indexOf(b))
    }));
  };

  const handleSave = () => {
    onSave(option.id, formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-800">Edit Option</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Option Text</label>
            <input
              type="text"
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Part */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Part / Component</label>
            <input
              type="text"
              value={formData.part}
              onChange={(e) => setFormData(prev => ({ ...prev, part: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Display, Battery, Logic Board"
            />
          </div>

          {/* Severity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <div className="flex flex-wrap gap-2">
              {['Good', 'Low', 'Medium', 'High', 'Salvage'].map(sev => (
                <button
                  key={sev}
                  onClick={() => setFormData(prev => ({ ...prev, severity: sev }))}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-all ${
                    formData.severity === sev
                      ? SEVERITY_COLORS[sev]
                      : 'border-gray-200 text-gray-500 hover:border-gray-400'
                  }`}
                >
                  {sev}
                </button>
              ))}
            </div>
          </div>

          {/* Weight & Multiplier */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight %</label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData(prev => ({ ...prev, weight: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.5"
                min="0"
                max="100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Multiplier</label>
              <input
                type="number"
                value={formData.multiplier}
                onChange={(e) => setFormData(prev => ({ ...prev, multiplier: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                step="0.25"
                min="0"
                max="1"
              />
            </div>
          </div>

          {/* Grade Eligibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Grade Eligibility</label>
            <div className="flex gap-2">
              {gradeHierarchy.map(grade => (
                <button
                  key={grade}
                  onClick={() => handleGradeToggle(grade)}
                  className={`w-10 h-10 rounded-lg font-bold text-sm transition-all ${
                    formData.grades.includes(grade)
                      ? `${GRADE_COLORS[grade]} text-white`
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {grade}
                </button>
              ))}
            </div>
          </div>

          {/* Special fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Top-up (₹)</label>
              <input
                type="number"
                value={formData.topup || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, topup: e.target.value ? parseInt(e.target.value) : null }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="For packaging items"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fixed Price (₹)</label>
              <input
                type="number"
                value={formData.fixed_price || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, fixed_price: e.target.value ? parseInt(e.target.value) : null }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="For salvage items"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560]"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== Add Option Modal ====================
const AddOptionModal = ({ categoryId, parameterId, parameterName, onClose, onAdd, gradeHierarchy }) => {
  const [formData, setFormData] = useState({
    text: '',
    part: '',
    severity: 'Good',
    weight: 0,
    multiplier: 0,
    grades: [...gradeHierarchy]
  });

  const handleAdd = () => {
    onAdd(categoryId, parameterId, formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-800">Add New Option</h2>
            <p className="text-sm text-gray-500">to {parameterName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Option Text *</label>
            <input
              type="text"
              value={formData.text}
              onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., Working, Not Working, etc."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Part / Component</label>
            <input
              type="text"
              value={formData.part}
              onChange={(e) => setFormData(prev => ({ ...prev, part: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
            <div className="flex flex-wrap gap-2">
              {['Good', 'Low', 'Medium', 'High', 'Salvage'].map(sev => (
                <button
                  key={sev}
                  onClick={() => setFormData(prev => ({ ...prev, severity: sev }))}
                  className={`px-3 py-1 rounded-full text-sm font-medium border transition-all ${
                    formData.severity === sev
                      ? SEVERITY_COLORS[sev]
                      : 'border-gray-200 text-gray-500'
                  }`}
                >
                  {sev}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight %</label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData(prev => ({ ...prev, weight: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg"
                step="0.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Multiplier</label>
              <input
                type="number"
                value={formData.multiplier}
                onChange={(e) => setFormData(prev => ({ ...prev, multiplier: parseFloat(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded-lg"
                step="0.25"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!formData.text}
            className="px-4 py-2 bg-[#2b3380] text-white rounded-lg hover:bg-[#1e2560] disabled:bg-gray-300"
          >
            Add Option
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== RBP Calculator Panel (Full QC) ====================
const RBPCalculator = ({ template, gradeHierarchy, gradeNames }) => {
  const [responses, setResponses] = useState({});
  const [mbp, setMbp] = useState(20000);
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Initialize with all categories expanded
  useEffect(() => {
    if (template?.categories) {
      const expanded = {};
      template.categories.forEach(cat => {
        expanded[cat.id] = true;
      });
      setExpandedCategories(expanded);
    }
  }, [template]);

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await fetch(`${API}/api/qc-config/test-calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ responses, mbp })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data);
      }
    } catch (err) {
      console.error('Calculate error:', err);
    }
    setCalculating(false);
  };

  const handleOptionSelect = (paramId, optionId) => {
    setResponses(prev => ({ ...prev, [paramId]: optionId }));
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Quick Fill All Good - selects all "Good" severity options
  const handleFillAllGood = () => {
    const goodResponses = {};
    template?.categories?.forEach(cat => {
      cat.parameters?.forEach(param => {
        // Find the first "Good" severity option for this parameter
        const goodOption = param.options?.find(opt => opt.severity === 'Good');
        if (goodOption) {
          goodResponses[param.id] = goodOption.id;
        }
      });
    });
    setResponses(goodResponses);
    setResult(null); // Clear previous result
  };

  // Quick Fill All Worst - selects worst severity options (for testing minimum price)
  const handleFillAllWorst = () => {
    const worstResponses = {};
    template?.categories?.forEach(cat => {
      cat.parameters?.forEach(param => {
        // Find the worst severity option (Salvage > High > Medium > Low > Good)
        const severityOrder = ['Salvage', 'High', 'Medium', 'Low', 'Good'];
        let worstOption = null;
        for (const severity of severityOrder) {
          worstOption = param.options?.find(opt => opt.severity === severity);
          if (worstOption) break;
        }
        if (worstOption) {
          worstResponses[param.id] = worstOption.id;
        }
      });
    });
    setResponses(worstResponses);
    setResult(null);
  };

  const handleReset = () => {
    setResponses({});
    setResult(null);
  };

  const answeredCount = Object.keys(responses).length;
  const totalParams = template?.categories?.reduce((sum, cat) => sum + (cat.parameters?.length || 0), 0) || 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="p-4 border-b bg-gradient-to-r from-[#2b3380] to-[#4a52a3]">
        <h3 className="font-bold text-white flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          RBP Calculator
        </h3>
        <p className="text-blue-100 text-xs mt-1">
          Revised Buyback Price • {answeredCount}/{totalParams} answered
        </p>
      </div>

      <div className="p-4">
        {/* MBP Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">MBP (Maximum Buyback Price)</label>
          <input
            type="number"
            value={mbp}
            onChange={(e) => setMbp(parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* All QC Parameters */}
        <div className="max-h-[400px] overflow-y-auto space-y-2 mb-4 border rounded-lg">
          {template?.categories?.map(cat => (
            <div key={cat.id} className="border-b last:border-b-0">
              <button
                onClick={() => toggleCategory(cat.id)}
                className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 text-left"
              >
                <div className="flex items-center gap-2">
                  <span>{cat.icon}</span>
                  <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                  <span className="text-xs text-gray-400">
                    ({cat.parameters?.filter(p => responses[p.id])?.length || 0}/{cat.parameters?.length || 0})
                  </span>
                </div>
                {expandedCategories[cat.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              
              {expandedCategories[cat.id] && (
                <div className="p-2 space-y-2 bg-white">
                  {cat.parameters?.map(param => (
                    <div key={param.id} className="border-l-2 border-gray-200 pl-2">
                      <p className="text-xs font-medium text-gray-600 mb-1">{param.name}</p>
                      <div className="flex flex-wrap gap-1">
                        {param.options?.map(opt => {
                          const isSelected = responses[param.id] === opt.id;
                          const severityBg = {
                            'Good': isSelected ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100',
                            'Low': isSelected ? 'bg-yellow-600 text-white' : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100',
                            'Medium': isSelected ? 'bg-orange-600 text-white' : 'bg-orange-50 text-orange-700 hover:bg-orange-100',
                            'High': isSelected ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100',
                            'Salvage': isSelected ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }[opt.severity] || (isSelected ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200');
                          
                          return (
                            <button
                              key={opt.id}
                              onClick={() => handleOptionSelect(param.id, opt.id)}
                              className={`px-2 py-0.5 text-xs rounded transition-colors ${severityBg}`}
                            >
                              {opt.text}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Quick Fill Actions */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={handleFillAllGood}
            className="flex-1 py-1.5 text-xs bg-green-100 text-green-700 rounded-lg font-medium hover:bg-green-200 transition-colors"
          >
            Fill All Good
          </button>
          <button
            onClick={handleFillAllWorst}
            className="flex-1 py-1.5 text-xs bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors"
          >
            Fill All Worst
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={handleCalculate}
            disabled={calculating || answeredCount === 0}
            className="flex-1 py-2 bg-[#2b3380] text-white rounded-lg font-medium disabled:bg-gray-300 hover:bg-[#1e2560] transition-colors"
          >
            {calculating ? 'Calculating...' : 'Calculate RBP'}
          </button>
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Reset
          </button>
        </div>

        {/* Result */}
        {result && (
          <div className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Grade</span>
              <span className={`px-4 py-1.5 rounded-full text-white font-bold text-lg ${GRADE_COLORS[result.grade]}`}>
                {result.grade} ({result.grade_name})
              </span>
            </div>
            
            <div className="flex items-center justify-between border-t pt-3">
              <span className="text-sm text-gray-500">MBP</span>
              <span className="text-lg text-gray-700">₹{result.mbp?.toLocaleString()}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Deduction</span>
              <span className="text-lg text-red-600">-₹{result.total_deduction?.toLocaleString()} ({result.total_deduction_percent}%)</span>
            </div>
            
            {result.total_topup > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Packaging Top-up</span>
                <span className="text-lg text-green-600">+₹{result.total_topup?.toLocaleString()}</span>
              </div>
            )}
            
            <div className="flex items-center justify-between border-t pt-3">
              <span className="font-bold text-gray-700">Final RBP</span>
              <span className="text-2xl font-bold text-green-600">₹{result.final_price?.toLocaleString()}</span>
            </div>

            {/* Warranty & Selling Info */}
            <div className="grid grid-cols-2 gap-2 pt-2 border-t">
              <div className="bg-white p-2 rounded text-center">
                <p className="text-xs text-gray-500">Warranty</p>
                <p className="font-medium text-gray-800">{result.warranty_tier}</p>
              </div>
              <div className="bg-white p-2 rounded text-center">
                <p className="text-xs text-gray-500">Selling Grade</p>
                <p className="font-medium text-gray-800">{result.selling_grade}</p>
              </div>
            </div>

            {/* Deductions Breakdown */}
            {result.deductions && result.deductions.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs font-medium text-gray-500 mb-2">Deductions Breakdown:</p>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {result.deductions.map((d, idx) => (
                    <div key={idx} className="flex justify-between text-xs">
                      <span className="text-gray-600">{d.parameter}: {d.option}</span>
                      <span className="text-red-600">-₹{d.amount?.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Parts Needed */}
            {result.parts_needed && result.parts_needed.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs font-medium text-gray-500 mb-1">Parts Needed:</p>
                <div className="flex flex-wrap gap-1">
                  {result.parts_needed.map((part, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs">
                      {part}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== Version History Panel ====================
const VersionHistory = ({ onRestore }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/api/qc-config/history`);
      const data = await res.json();
      if (data.success) {
        setHistory(data.history);
      }
    } catch (err) {
      console.error('History error:', err);
    }
    setLoading(false);
  };

  const handleRestore = async (version) => {
    if (!window.confirm(`Restore to version ${version}?`)) return;
    
    try {
      const res = await fetch(`${API}/api/qc-config/history/${version}/restore`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        alert('Restored successfully!');
        onRestore();
      }
    } catch (err) {
      console.error('Restore error:', err);
    }
  };

  if (loading) return <div className="p-4 text-gray-500">Loading history...</div>;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
        <History className="w-5 h-5" />
        Version History
      </h3>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {history.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm font-medium">v{item.version}</p>
              <p className="text-xs text-gray-500">{item.change_description}</p>
              <p className="text-xs text-gray-400">
                {new Date(item.created_at).toLocaleString()}
              </p>
            </div>
            <button
              onClick={() => handleRestore(item.version)}
              className="px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
            >
              Restore
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==================== Main Component ====================
export default function PQCConfiguration() {
  const [template, setTemplate] = useState(null);
  const [stats, setStats] = useState(null);
  const [gradeHierarchy, setGradeHierarchy] = useState([]);
  const [gradeNames, setGradeNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [addModal, setAddModal] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const fetchTemplate = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/qc-config/template`);
      const data = await res.json();
      if (data.success) {
        setTemplate(data.template);
        setStats(data.stats);
        setGradeHierarchy(data.grade_hierarchy);
        setGradeNames(data.grade_names || {});
        // Expand all categories by default
        const expanded = {};
        data.template.categories.forEach(cat => {
          expanded[cat.id] = true;
        });
        setExpandedCategories(expanded);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTemplate();
  }, [fetchTemplate]);

  const handleOptionUpdate = async (optionId, updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/qc-config/option/${optionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        fetchTemplate(); // Refresh
      }
    } catch (err) {
      console.error('Update error:', err);
    }
    setSaving(false);
  };

  const handleInlineUpdate = async (optionId, field, value) => {
    await handleOptionUpdate(optionId, { [field]: value });
  };

  const handleAddOption = async (categoryId, parameterId, optionData) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/qc-config/option`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_id: categoryId,
          parameter_id: parameterId,
          ...optionData
        })
      });
      const data = await res.json();
      if (data.success) {
        fetchTemplate();
      }
    } catch (err) {
      console.error('Add error:', err);
    }
    setSaving(false);
  };

  const handleDeleteOption = async (optionId) => {
    if (!window.confirm('Delete this option?')) return;
    
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/qc-config/option/${optionId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        fetchTemplate();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
    setSaving(false);
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/api/qc-config/export`);
      const data = await res.json();
      if (data.success) {
        const blob = new Blob([JSON.stringify(data.template, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qc-template-${data.template.version || 'v1'}.json`;
        a.click();
      }
    } catch (err) {
      console.error('Export error:', err);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        if (window.confirm('Import and replace current template?')) {
          const res = await fetch(`${API}/api/qc-config/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ template: imported, overwrite: true })
          });
          const data = await res.json();
          if (data.success) {
            alert('Imported successfully!');
            fetchTemplate();
          }
        }
      } catch (err) {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  };

  const toggleCategory = (catId) => {
    setExpandedCategories(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  // Filter options by search
  const filterTemplate = (template) => {
    if (!searchQuery) return template;
    
    const query = searchQuery.toLowerCase();
    return {
      ...template,
      categories: template.categories.map(cat => ({
        ...cat,
        parameters: cat.parameters.map(param => ({
          ...param,
          options: param.options.filter(opt => 
            opt.text.toLowerCase().includes(query) ||
            opt.part?.toLowerCase().includes(query) ||
            opt.severity?.toLowerCase().includes(query)
          )
        })).filter(param => param.options.length > 0)
      })).filter(cat => cat.parameters.length > 0)
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2b3380]"></div>
      </div>
    );
  }

  const filteredTemplate = template ? filterTemplate(template) : null;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">PQC Configuration</h1>
            <p className="text-gray-500">
              Pickup QC Template • {stats?.total_categories} Categories • {stats?.total_parameters} Parameters • {stats?.total_options} Options
              {template?.version && <span className="ml-2 text-blue-600">v{template.version}</span>}
            </p>
            <p className="text-xs text-green-600 mt-1">✓ Changes sync in real-time to Engineer PWA</p>
          </div>
          
          <div className="flex items-center gap-3">
            {saving && (
              <span className="text-sm text-blue-600 flex items-center gap-1">
                <RefreshCw className="w-4 h-4 animate-spin" />
                Saving...
              </span>
            )}
            
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Version History"
            >
              <History className="w-5 h-5 text-gray-600" />
            </button>
            
            <button
              onClick={handleExport}
              className="p-2 hover:bg-gray-100 rounded-lg"
              title="Export JSON"
            >
              <Download className="w-5 h-5 text-gray-600" />
            </button>
            
            <label className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer" title="Import JSON">
              <Upload className="w-5 h-5 text-gray-600" />
              <input type="file" accept=".json" onChange={handleImport} className="hidden" />
            </label>
          </div>
        </div>

        {/* Search */}
        <div className="mt-4 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search options..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex gap-6">
        {/* Main Content */}
        <div className="flex-1 space-y-4">
          {filteredTemplate?.categories?.map(category => {
            const CategoryIcon = CATEGORY_ICONS[category.id] || Settings;
            const isExpanded = expandedCategories[category.id];
            
            return (
              <div key={category.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.icon}</span>
                    <div className="text-left">
                      <h2 className="font-bold text-gray-800">{category.name}</h2>
                      <p className="text-sm text-gray-500">
                        {category.param_count} parameters • {category.option_count} options
                      </p>
                    </div>
                  </div>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>

                {/* Parameters Table */}
                {isExpanded && (
                  <div className="p-4">
                    {category.parameters?.map(param => (
                      <div key={param.id} className="mb-4 last:mb-0">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-semibold text-gray-700">{param.name}</h3>
                          <button
                            onClick={() => setAddModal({ categoryId: category.id, parameterId: param.id, parameterName: param.name })}
                            className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded flex items-center gap-1"
                          >
                            <Plus className="w-3 h-3" /> Add Option
                          </button>
                        </div>
                        
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-gray-600">
                              <th className="text-left py-2 px-3 font-medium">Option</th>
                              <th className="text-left py-2 px-3 font-medium">Severity</th>
                              <th className="text-left py-2 px-3 font-medium">Part</th>
                              <th className="text-center py-2 px-3 font-medium">Weight %</th>
                              <th className="text-center py-2 px-3 font-medium">Multiplier</th>
                              <th className="text-left py-2 px-3 font-medium">Grade</th>
                              <th className="text-center py-2 px-3 font-medium w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {param.options?.map(option => (
                              <tr key={option.id} className="border-t border-gray-100 hover:bg-gray-50">
                                <td className="py-2 px-3 font-medium text-gray-800">{option.text}</td>
                                <td className="py-2 px-3">
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_COLORS[option.severity]}`}>
                                    {option.severity}
                                  </span>
                                </td>
                                <td className="py-2 px-3 text-gray-600">{option.part || '-'}</td>
                                <td className="py-2 px-3 text-center">
                                  <InlineEdit
                                    value={option.weight}
                                    type="number"
                                    suffix="%"
                                    onSave={(val) => handleInlineUpdate(option.id, 'weight', val)}
                                  />
                                </td>
                                <td className="py-2 px-3 text-center">
                                  <InlineEdit
                                    value={option.multiplier}
                                    type="number"
                                    onSave={(val) => handleInlineUpdate(option.id, 'multiplier', val)}
                                  />
                                </td>
                                <td className="py-2 px-3">
                                  <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                                    {option.grade_range || 'Z1-Z7'}
                                  </span>
                                </td>
                                <td className="py-2 px-3">
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={() => setEditModal(option)}
                                      className="p-1 hover:bg-blue-50 rounded text-blue-600"
                                      title="Edit"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteOption(option.id)}
                                      className="p-1 hover:bg-red-50 rounded text-red-600"
                                      title="Delete"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Sidebar */}
        <div className="w-96 space-y-4">
          <RBPCalculator template={template} gradeHierarchy={gradeHierarchy} gradeNames={gradeNames} />
          {showHistory && <VersionHistory onRestore={fetchTemplate} />}
        </div>
      </div>

      {/* Modals */}
      {editModal && (
        <OptionEditModal
          option={editModal}
          onClose={() => setEditModal(null)}
          onSave={handleOptionUpdate}
          gradeHierarchy={gradeHierarchy}
        />
      )}
      
      {addModal && (
        <AddOptionModal
          categoryId={addModal.categoryId}
          parameterId={addModal.parameterId}
          parameterName={addModal.parameterName}
          onClose={() => setAddModal(null)}
          onAdd={handleAddOption}
          gradeHierarchy={gradeHierarchy}
        />
      )}
    </div>
  );
}
